export type ImagePreviewFailureKind = 'decode' | 'validation';

const IMAGE_PREVIEW_FAILURE = Symbol.for('creator.image.previewFailure');

/**
 * Read the local-preview failure classification attached to an error.
 *
 * @param error Value that may represent a browser decode/validation failure.
 * @returns The classified failure kind, or undefined for unrelated exceptions.
 */
export function getImagePreviewFailureKind(error: unknown): ImagePreviewFailureKind | undefined {
	if (!error || typeof error !== 'object') {
		return undefined;
	}

	try {
		return (
			error as Record<symbol, ImagePreviewFailureKind | undefined>
		)[IMAGE_PREVIEW_FAILURE];
	} catch {
		return undefined;
	}
}

/**
 * Mark an error as evidence that the browser cannot currently preview a source.
 *
 * @param error Error-like value to classify.
 * @param kind Decode or decoded-image validation failure.
 * @returns The original value, marked in place when it is an object.
 */
export function markImagePreviewFailure<E>(
	error: E,
	kind: ImagePreviewFailureKind,
): E {
	if (
		error
		&& typeof error === 'object'
	) {
		try {
			Object.defineProperty(
				error,
				IMAGE_PREVIEW_FAILURE,
				{
					configurable: true,
					value: kind,
				},
			);
		} catch {
			/**
			 * Failure classification is best-effort and must never mask the error.
			 */
		}
	}

	return error;
}
